import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { SPECIALTIES, SPECIALTY_ORDER } from './data/specialties.data';
import { GIVE_UP_MS, SiteDataState, departmentSlugOf, normalizeName } from './site-utils';

export interface SiteDepartmentCard {
  id: string;
  name: string;
  tagLine: string;
  description: string;
  slug: string;
  hasSpecialtyPage: boolean;
  iconSvg: string;
  logo: string | null;
}

const LOGO_CATEGORY = 'Department logo';
const DEFAULT_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 5v14M5 12h14"></path></svg>';

@Injectable()
export class SiteDepartmentService implements OnDestroy {
  readonly state$ = new BehaviorSubject<SiteDataState>('loading');
  readonly cards$ = new BehaviorSubject<SiteDepartmentCard[]>([]);

  private key = '';
  private readonly giveUpTimer: number;

  constructor(private http: HttpClient) {
    this.giveUpTimer = window.setTimeout(() => {
      if (this.state$.value === 'loading') { this.state$.next('fallback'); }
    }, GIVE_UP_MS);
  }

  ngOnDestroy() { window.clearTimeout(this.giveUpTimer); }

  configure(tenantId: string, branchId: string) {
    if (!tenantId || !branchId || this.key === tenantId + '/' + branchId) { return; }
    this.key = tenantId + '/' + branchId;
    this.load(tenantId, branchId);
  }

  findBySlug(slug: string): SiteDepartmentCard | undefined {
    return this.cards$.value.find(c => c.slug === slug);
  }

  private load(tenantId: string, branchId: string) {
    const url = `${environment.apiUrl}/tenants/${tenantId}/departments/getDepartmentMaster`;
    this.http.get<any>(url, { params: { branchId }, headers: { Accept: 'application/vnd.ezovion.hateoas+json' } }).subscribe(response => {
      const cards = this.toCards(response && response.details);
      if (!cards.length) { this.state$.next('fallback'); return; }
      this.cards$.next(cards);
      this.state$.next('ready');
      this.loadLogos(tenantId, branchId, cards);
    }, () => this.state$.next('fallback'));
  }

  private toCards(details: any[]): SiteDepartmentCard[] {
    const cards = (details || [])
      .filter(d => d && d.isActive === true && ((d.tagLine || '').trim() || (d.description || '').trim()))
      .map(d => {
        const specialty = SPECIALTIES.find(s => normalizeName(s.name) === normalizeName(d.name));
        return {
          id: d.id,
          name: d.name,
          tagLine: (d.tagLine || '').trim(),
          description: (d.description || '').trim(),
          slug: departmentSlugOf(d.id, d.name),
          hasSpecialtyPage: !!specialty,
          iconSvg: specialty ? specialty.icon : DEFAULT_ICON,
          logo: null
        } as SiteDepartmentCard;
      });
    const rank = (c: SiteDepartmentCard) => c.hasSpecialtyPage ? SPECIALTY_ORDER.indexOf(c.slug) : SPECIALTY_ORDER.length;
    return cards.sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
  }

  private loadLogos(tenantId: string, branchId: string, cards: SiteDepartmentCard[]) {
    const base = `${environment.apiUrl}/ImageAtchDet/${tenantId}/${branchId}`;
    this.http.get<any>(`${environment.apiUrl}/publicsite/${tenantId}/${branchId}/department-logos`).subscribe(
      response => this.downloadLogos(base, cards, (response && response.details) || []),
      () => this.http.get<any[]>(`${base}/all`).subscribe(records => this.downloadLogos(base, cards, records || []), () => {}));
  }

  private downloadLogos(base: string, cards: SiteDepartmentCard[], records: any[]) {
    cards.forEach(card => {
      const record = records.find(x => x.category === LOGO_CATEGORY && x.phyId == null && x.empId == null && x.patId == null
        && x.fileName && x.fileName.toLowerCase().startsWith((card.id + '.').toLowerCase()));
      if (!record) { return; }
      this.http.post<string>(`${base}/download`, { ...record, isCreate: true, isEdit: true }).subscribe(image => {
        if (!image) { return; }
        card.logo = 'data:image/jpeg;base64,' + image;
        this.cards$.next(this.cards$.value.slice());
      }, () => {});
    });
  }
}
